/* Announcement bar v2 (sections/main-announcement-bar-v2.liquid)
 * Rotates the enabled messages. Pauses while the bar is hovered or holds
 * keyboard focus, while the tab is hidden, and while a message block is
 * selected in the theme editor. Optional arrows step back and forth and
 * restart the timer. */
if (!customElements.get('announcement-bar-v2')) {
  class AnnouncementBarV2 extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('.announcement-bar-v2__message'));
      this.index = Math.max(0, this.messages.findIndex((m) => m.classList.contains('is-active')));
      this.interval = parseInt(this.dataset.interval, 10) || 4000;
      this.pauseReasons = new Set();

      this.onPointerEnter = () => this.pause('hover');
      this.onPointerLeave = () => this.resume('hover');
      this.onFocusIn = (event) => {
        // Keyboard focus only: a mouse click on an arrow should not freeze the bar.
        if (event.target.matches(':focus-visible')) this.pause('focus');
      };
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      };
      this.onVisibilityChange = () => {
        document.hidden ? this.pause('hidden') : this.resume('hidden');
      };
      this.onBlockSelect = (event) => {
        const target = this.messages.findIndex((m) => m.dataset.blockId === event.detail.blockId);
        if (target === -1) return;
        this.show(target);
        this.pause('editor');
      };
      this.onBlockDeselect = () => this.resume('editor');
      this.onArrowClick = (event) => {
        const arrow = event.target.closest('.announcement-bar-v2__arrow');
        if (!arrow) return;
        const step = arrow.dataset.direction === 'previous' ? -1 : 1;
        this.show((this.index + step + this.messages.length) % this.messages.length);
        this.start();
      };

      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('click', this.onArrowClick);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('click', this.onArrowClick);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    show(index) {
      if (index === this.index) return;
      this.messages[this.index].classList.remove('is-active');
      this.index = index;
      this.messages[this.index].classList.add('is-active');
    }

    next() {
      this.show((this.index + 1) % this.messages.length);
    }

    start() {
      this.stop();
      if (this.messages.length < 2 || this.pauseReasons.size > 0) return;
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.stop();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.start();
    }
  }

  customElements.define('announcement-bar-v2', AnnouncementBarV2);
}
